import clsx from "clsx";
import { useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";

import { useActivityPing } from "../../hooks/useActivityPing";
import { CommandPalette } from "../CommandPalette";

import Sidebar from "./Sidebar/Sidebar";
import { TopNavbar } from "./TopNavbar";
import { useWorkspaceViewport } from "./useWorkspaceViewport";

export function DashboardLayout() {
  const pageRef = useWorkspaceViewport<HTMLElement>();
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem("sms.sidebar.collapsed") === "true";
    } catch {
      return false;
    }
  });
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  // Send periodic activity pings while authenticated
  useActivityPing();

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    // Keep modal state in sync with Tailwind's desktop navigation breakpoint.
    const desktop = window.matchMedia("(min-width: 1024px)");
    const closeOnDesktop = () => {
      if (desktop.matches) setIsMobileMenuOpen(false);
    };
    closeOnDesktop();
    desktop.addEventListener("change", closeOnDesktop);
    return () => desktop.removeEventListener("change", closeOnDesktop);
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem("sms.sidebar.collapsed", String(collapsed));
    } catch {
      /* Storage is optional. */
    }
  }, [collapsed]);

  return (
    <div
      className="app-shell min-h-screen"
      style={{ "--sidebar-offset": collapsed ? "4rem" : "14rem" } as React.CSSProperties}
    >
      {/* Sidebar */}
      <Sidebar
        collapsed={collapsed}
        isMobileMenuOpen={isMobileMenuOpen}
        onMobileMenuClose={() => setIsMobileMenuOpen(false)}
      />

      {/* Main content */}
      <div
        className={clsx(
          "min-w-0 transition-[margin] duration-200 ease-out",
          collapsed ? "lg:ml-16" : "lg:ml-56",
          "min-h-screen",
        )}
      >
        <CommandPalette />
        {/* Top navigation */}
        <TopNavbar
          onSidebarToggle={() => setCollapsed(!collapsed)}
          onMobileMenuToggle={() => setIsMobileMenuOpen(true)}
          sidebarCollapsed={collapsed}
        />

        {/* Page content */}
        <main
          ref={pageRef}
          id="main-content"
          className={clsx(
            "workspace-page-content min-w-0",
            !/^\/students-v2(?:\/|$)/i.test(location.pathname) && "ui-standard",
          )}
          tabIndex={-1}
        >
          <Outlet />
        </main>
      </div>
    </div>
  );
}
